import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

/**
 * Visor reutilizable para las imágenes de la tienda pública.
 * - Usa el Dialog de MUI (portal + bloqueo de scroll + manejo de foco/Escape).
 * - Cada instancia tiene su propio índice y no interfiere con otros carruseles.
 * - Las imágenes se muestran completas, sin aplicar el recorte del carrusel.
 */
export default function FullscreenImageViewer({
  open = false,
  images = [],
  initialIndex = 0,
  title = "Galería de imágenes",
  onClose,
  onImageChange,
  onImageError,
}) {
  const photos = useMemo(
    () => (Array.isArray(images) ? images : [])
      .map((item) => (typeof item === "string" ? { src: item } : item))
      .filter((item) => typeof item?.src === "string" && item.src.trim()),
    [images],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const touchOrigin = useRef(null);
  const thumbnailRefs = useRef([]);
  const count = photos.length;

  useEffect(() => {
    if (!open) return;
    const next = Number.isFinite(Number(initialIndex)) ? Math.trunc(Number(initialIndex)) : 0;
    setActiveIndex(Math.max(0, Math.min(next, Math.max(0, count - 1))));
    touchOrigin.current = null;
  }, [open, initialIndex, count]);

  const navigateTo = (nextIndex) => {
    if (!count) return;
    const target = (nextIndex + count) % count;
    setActiveIndex(target);
    onImageChange?.(photos[target], target);
  };

  useEffect(() => {
    if (!open || count < 2) return undefined;
    const onKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        event.preventDefault();
        const step = event.key === "ArrowRight" ? 1 : -1;
        setActiveIndex((current) => {
          const target = (current + step + count) % count;
          onImageChange?.(photos[target], target);
          return target;
        });
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, count, onImageChange, photos]);

  useEffect(() => {
    if (!open || count < 2) return;
    thumbnailRefs.current[activeIndex]?.scrollIntoView?.({
      behavior: "smooth", inline: "center", block: "nearest",
    });
  }, [activeIndex, open, count]);

  const onTouchStart = (event) => {
    if (event.touches.length !== 1) {
      touchOrigin.current = null;
      return;
    }
    touchOrigin.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  };
  const onTouchEnd = (event) => {
    const start = touchOrigin.current;
    touchOrigin.current = null;
    if (!start || !count || event.changedTouches.length !== 1) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    // Sólo gestos horizontales deliberados: no interrumpir el scroll vertical.
    if (Math.abs(dx) >= 55 && Math.abs(dx) > Math.abs(dy) * 1.35) {
      navigateTo(activeIndex + (dx < 0 ? 1 : -1));
    }
  };

  const active = photos[activeIndex];
  const showControls = count > 1;
  const controlSx = {
    width: { xs: 42, sm: 48 },
    height: { xs: 42, sm: 48 },
    color: "#FFFFFF",
    bgcolor: "rgba(255,255,255,.12)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,.16)",
    "&:hover": { bgcolor: "rgba(255,255,255,.24)" },
    "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: 2 },
  };

  return (
    <Dialog
      open={Boolean(open && count)}
      onClose={onClose}
      fullScreen
      aria-label={title}
      PaperProps={{
        sx: {
          m: 0,
          bgcolor: "#0C0E13",
          color: "#FFFFFF",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          "@supports (height: 100dvh)": { height: "100dvh" },
          pt: "env(safe-area-inset-top, 0px)",
          pb: "env(safe-area-inset-bottom, 0px)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ minHeight: { xs: 62, sm: 70 }, px: { xs: 2, sm: 3 }, flexShrink: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.67)" }} aria-live="polite">
              {count ? `${activeIndex + 1} de ${count}` : ""}
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar pantalla completa" onClick={onClose} sx={controlSx}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Box
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onTouchCancel={() => { touchOrigin.current = null; }}
          sx={{
            display: "flex", alignItems: "center", justifyContent: "center",
            flex: "1 1 auto", minHeight: 0, minWidth: 0,
            position: "relative", px: { xs: 1, sm: 8, lg: 12 },
            touchAction: "pan-y", userSelect: "none",
          }}
        >
          {active ? (
            <Box
              key={`${active.src}-${activeIndex}`}
              component="img"
              src={active.src}
              alt={active.alt || active.caption || title}
              onError={onImageError}
              draggable={false}
              decoding="async"
              sx={{
                display: "block", maxWidth: "100%", maxHeight: "100%",
                height: "auto", width: "auto", objectFit: "contain",
                borderRadius: 0.5, userSelect: "none",
              }}
            />
          ) : null}
          {showControls ? (
            <>
              <IconButton
                aria-label="Imagen anterior"
                onClick={() => navigateTo(activeIndex - 1)}
                sx={{ ...controlSx, position: "absolute", left: { xs: 8, sm: 16 }, top: "50%", transform: "translateY(-50%)" }}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
              <IconButton
                aria-label="Imagen siguiente"
                onClick={() => navigateTo(activeIndex + 1)}
                sx={{ ...controlSx, position: "absolute", right: { xs: 8, sm: 16 }, top: "50%", transform: "translateY(-50%)" }}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
            </>
          ) : null}
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1, pb: { xs: 1.5, sm: 2 }, flexShrink: 0, minHeight: 32 }}>
          {active?.caption ? (
            <Typography sx={{ fontSize: { xs: 13, sm: 15 }, lineHeight: 1.5, textAlign: "center", color: "rgba(255,255,255,.85)", mb: 1, maxWidth: 900, mx: "auto" }}>
              {active.caption}
            </Typography>
          ) : null}
          {showControls ? (
            <Stack
              direction="row" spacing={1} justifyContent="flex-start"
              sx={{ overflowX: "auto", width: "max-content", maxWidth: "100%", mx: "auto", py: 0.5, scrollbarWidth: "thin", scrollbarColor: "#707070 transparent" }}
            >
              {photos.map((photo, index) => (
                <Box
                  key={`${photo.src}-${index}`}
                  ref={(node) => { thumbnailRefs.current[index] = node; }}
                  component="button" type="button"
                  onClick={() => navigateTo(index)}
                  aria-label={`Mostrar imagen ${index + 1} en pantalla completa`}
                  aria-pressed={index === activeIndex}
                  sx={{
                    display: "block", flex: "0 0 auto", width: { xs: 46, sm: 56 },
                    height: { xs: 46, sm: 56 }, cursor: "pointer", p: 0.35,
                    borderRadius: 1.5, bgcolor: "rgba(255,255,255,.08)",
                    border: index === activeIndex ? "2px solid #FFFFFF" : "2px solid transparent",
                    opacity: index === activeIndex ? 1 : 0.65,
                    transition: "opacity .15s ease, border-color .15s ease",
                    "&:hover": { opacity: 1 },
                    "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: 2 },
                  }}
                >
                  <Box component="img" src={photo.src} alt="" onError={onImageError} loading="lazy" decoding="async" draggable={false} sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover", borderRadius: 0.8 }} />
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Dialog>
  );
}
